import React, { useEffect, useState } from 'react';
import { Apple, Check, Plus, Search, Trash2, X } from 'lucide-react';
import { adminApi } from '../../services/adminApi';

const CATEGORIES = [
  'Proteins',
  'Grains',
  'Legumes',
  'Produce',
  'Fruits',
  'Dairy',
  'Fats',
  'Recipes',
];

const EMPTY_FOOD = {
  name_en: '',
  name_es: '',
  name_it: '',
  category: 'Proteins',
  kcal_per_100g: 150,
  protein_per_100g: 20,
  carbs_per_100g: 5,
  fat_per_100g: 5,
  fiber_per_100g: 2,
  default_portion_name: '1 serving (150g)',
  default_portion_grams: 150,
  is_recipe: false,
  recipe_instructions: '',
};

const FoodDatabaseManager = () => {
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FOOD);
  const [feedback, setFeedback] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const list = await adminApi.getFoods();
      setFoods(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const flash = (msg) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(''), 3500);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const payload = {
      name_i18n: {
        en: form.name_en.trim() || form.name_es.trim(),
        es: form.name_es.trim() || form.name_en.trim(),
        it: form.name_it.trim() || form.name_es.trim() || form.name_en.trim(),
      },
      category: form.category,
      kcal_per_100g: Number(form.kcal_per_100g) || 0,
      protein_per_100g: Number(form.protein_per_100g) || 0,
      carbs_per_100g: Number(form.carbs_per_100g) || 0,
      fat_per_100g: Number(form.fat_per_100g) || 0,
      fiber_per_100g: Number(form.fiber_per_100g) || 0,
      default_portion_name: form.default_portion_name.trim() || '100g',
      default_portion_grams: Number(form.default_portion_grams) || 100,
      is_recipe: Boolean(form.is_recipe || form.category === 'Recipes'),
      recipe_instructions: form.recipe_instructions.trim() || null,
    };

    const created = await adminApi.createFood(payload);
    setFoods((prev) => [created, ...prev]);
    setShowModal(false);
    setForm(EMPTY_FOOD);
    flash('Added to Food & Recipe Database!');
  };

  const handleDelete = async (foodId) => {
    if (!window.confirm('Remove this food/recipe from the database?')) return;
    await adminApi.deleteFood(foodId);
    setFoods((prev) => prev.filter((f) => f.id !== foodId));
    flash('Removed from database.');
  };

  const filtered = foods.filter((f) => {
    if (category !== 'all' && f.category !== category) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (f.name_i18n?.en || '').toLowerCase().includes(q) ||
      (f.name_i18n?.es || '').toLowerCase().includes(q) ||
      (f.name_i18n?.it || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      {feedback && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          <Check className="w-4 h-4" />
          {feedback}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-50 text-amber-600">
              <Apple className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Clinical Food & Recipe Database ({foods.length})
              </h2>
              <p className="text-xs text-gray-500">
                Multilingual nutritional reference per 100g + household portions used in the Meal Plan Builder.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 bg-primary hover:bg-blue-600 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Custom Food / Recipe
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search foods or recipes (EN / ES / IT)…"
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {['all', ...CATEGORIES].map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setCategory(cat)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
                  category === cat
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat === 'all' ? 'All' : cat}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-400 py-8 text-center">Loading database…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase text-gray-400">
                  <th className="py-3 px-3">Food / Recipe (ES · EN · IT)</th>
                  <th className="py-3 px-3">Category</th>
                  <th className="py-3 px-3">Default Portion</th>
                  <th className="py-3 px-3 text-right">kcal / 100g</th>
                  <th className="py-3 px-3 text-right">Protein</th>
                  <th className="py-3 px-3 text-right">Carbs</th>
                  <th className="py-3 px-3 text-right">Fat</th>
                  <th className="py-3 px-3 text-right">Fiber</th>
                  <th className="py-3 px-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((f) => (
                  <tr key={f.id} className="hover:bg-gray-50/80">
                    <td className="py-3 px-3">
                      <div className="font-bold text-gray-900">
                        {f.name_i18n?.es || f.name_i18n?.en}
                      </div>
                      <div className="text-xs text-gray-400">
                        EN: {f.name_i18n?.en} · IT: {f.name_i18n?.it}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700">
                        {f.category}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-600">
                      {f.default_portion_name} ({f.default_portion_grams}g)
                    </td>
                    <td className="py-3 px-3 text-right font-bold text-gray-900">
                      {f.kcal_per_100g}
                    </td>
                    <td className="py-3 px-3 text-right text-blue-600 font-semibold">
                      {f.protein_per_100g}g
                    </td>
                    <td className="py-3 px-3 text-right text-amber-600 font-semibold">
                      {f.carbs_per_100g}g
                    </td>
                    <td className="py-3 px-3 text-right text-purple-600 font-semibold">
                      {f.fat_per_100g}g
                    </td>
                    <td className="py-3 px-3 text-right text-teal-600">
                      {f.fiber_per_100g}g
                    </td>
                    <td className="py-3 px-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(f.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg"
                        title="Delete food"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <form
            onSubmit={handleCreate}
            className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-xl w-full p-6 space-y-4"
          >
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="text-base font-bold text-gray-900">Add Custom Food or Recipe</h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-1 text-gray-400 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Name (Español) *
                </label>
                <input
                  type="text"
                  required
                  value={form.name_es}
                  onChange={(e) => setForm({ ...form, name_es: e.target.value })}
                  placeholder="Salmón al horno"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Name (English)
                </label>
                <input
                  type="text"
                  value={form.name_en}
                  onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                  placeholder="Baked Salmon"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Name (Italiano)
                </label>
                <input
                  type="text"
                  value={form.name_it}
                  onChange={(e) => setForm({ ...form, name_it: e.target.value })}
                  placeholder="Salmone al forno"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Category
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Default Portion Label
                </label>
                <input
                  type="text"
                  value={form.default_portion_name}
                  onChange={(e) => setForm({ ...form, default_portion_name: e.target.value })}
                  placeholder="1 fillet (150g)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Default Grams
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.default_portion_grams}
                  onChange={(e) => setForm({ ...form, default_portion_grams: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 bg-gray-50 p-3.5 rounded-xl border border-gray-200">
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">kcal / 100g</label>
                <input
                  type="number"
                  step="0.1"
                  required
                  value={form.kcal_per_100g}
                  onChange={(e) => setForm({ ...form, kcal_per_100g: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm font-bold bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-blue-600 mb-1">Protein (g)</label>
                <input
                  type="number"
                  step="0.1"
                  required
                  value={form.protein_per_100g}
                  onChange={(e) => setForm({ ...form, protein_per_100g: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-amber-600 mb-1">Carbs (g)</label>
                <input
                  type="number"
                  step="0.1"
                  required
                  value={form.carbs_per_100g}
                  onChange={(e) => setForm({ ...form, carbs_per_100g: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-purple-600 mb-1">Fat (g)</label>
                <input
                  type="number"
                  step="0.1"
                  required
                  value={form.fat_per_100g}
                  onChange={(e) => setForm({ ...form, fat_per_100g: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-teal-600 mb-1">Fiber (g)</label>
                <input
                  type="number"
                  step="0.1"
                  value={form.fiber_per_100g}
                  onChange={(e) => setForm({ ...form, fiber_per_100g: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                Recipe Preparation Notes (optional)
              </label>
              <textarea
                rows={2}
                value={form.recipe_instructions}
                onChange={(e) => setForm({ ...form, recipe_instructions: e.target.value })}
                placeholder="Preparation steps or clinical swaps…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-primary hover:bg-blue-600 text-white text-sm font-bold px-5 py-2 rounded-lg"
              >
                Save Food
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FoodDatabaseManager;
